/**
 * FERP Portal - Subgraph Constellation View (Architect View)
 *
 * LIFE-CRITICAL: Visualizes the federated GraphQL architecture
 * showing how subgraphs interconnect to form the complete system.
 *
 * FERP Federation Architecture (supergraph config):
 * - core: 25 primitives, metadata, approval workflows
 * - fhir: FHIR R4 healthcare data (formerly Medplum integration)
 * - payroll: Payroll processing, time tracking (formerly TimeTrex integration)
 * - erp: Accounting/ERP, general ledger (formerly OFBiz integration)
 * - wearables: Vuzix smart glasses, hands-free workflows
 * - website: Website builder, pages, deployments
 * - portal: External intake portals (DRR-073)
 * - pii-vault: Zero-trust PII encryption (separate AWS account)
 * - pentest: Security scanning (future)
 *
 * Apollo Router coordinates all queries across subgraphs.
 * Subgraph crates: ferp-fhir, ferp-payroll, ferp-erp, ferp-wearables, ferp-portal
 * Unified binary: ferp-subgraphs serves all domain subgraphs via feature flags
 *
 * NOTE: Real-time health data will be integrated once useGetSystemHealthQuery
 * is generated. For now, using static configuration data from subgraph metadata configs.
 */

import React, { useState, useMemo } from 'react';
import {
  Card,
  Group,
  Stack,
  Badge,
  Button,
  Modal,
  Tabs,
  SimpleGrid,
  ThemeIcon,
  Paper,
  Text,
  Title,
  Alert,
  Code,
  Divider,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconClock,
  IconRefresh,
  IconHeartbeat,
  IconCash,
  IconCalculator,
  IconShield,
  IconCloud,
  IconDeviceWatch,
  IconBrowser,
  IconForms,
  IconLock,
} from '@tabler/icons-react';
import { useTenantSubgraphConfig } from '@/hooks/useTenantSubgraphConfig';
import { useAuth } from '@/auth/AuthContext';
import { getIcon } from '@/utils/iconMap';
import { useRouterMetrics } from '@/hooks/useRouterMetrics';
import { ErrorTracePanel } from './ErrorTracePanel';

// Subgraph status configuration
const STATUS_CONFIG = {
  HEALTHY: { color: 'green', icon: IconCheck, label: 'Healthy' },
  DEGRADED: { color: 'yellow', icon: IconAlertTriangle, label: 'Degraded' },
  UNHEALTHY: { color: 'red', icon: IconX, label: 'Unhealthy' },
  UNKNOWN: { color: 'gray', icon: IconClock, label: 'Unknown' },
} as const;

// Subgraph type configuration — matches supergraph config + subgraph metadata configs
const SUBGRAPH_CONFIG = {
  core: {
    color: 'blue',
    icon: IconShield,
    label: 'FERP Core',
    description: '25 primitives, metadata DSL, approval workflows',
    domain: 'Core Platform',
  },
  fhir: {
    color: 'red',
    icon: IconHeartbeat,
    label: 'Healthcare (FHIR)',
    description: 'FHIR R4 healthcare data and clinical records',
    domain: 'Healthcare',
  },
  payroll: {
    color: 'green',
    icon: IconCash,
    label: 'Payroll',
    description: 'Payroll processing, time tracking, and tax compliance',
    domain: 'HR/Payroll',
  },
  erp: {
    color: 'violet',
    icon: IconCalculator,
    label: 'Accounting / ERP',
    description: 'General ledger, journal entries, and financial reporting (OFBiz)',
    domain: 'Finance/ERP',
  },
  wearables: {
    color: 'cyan',
    icon: IconDeviceWatch,
    label: 'Wearables (Vuzix)',
    description: 'Hands-free workflow execution via Vuzix smart glasses',
    domain: 'Devices',
  },
  website: {
    color: 'teal',
    icon: IconBrowser,
    label: 'Website Builder',
    description: 'Website builder, pages, components, deployments',
    domain: 'Web',
  },
  portal: {
    color: 'orange',
    icon: IconForms,
    label: 'Portal',
    description: 'External intake portals — form schemas to workflow instances (DRR-073)',
    domain: 'Intake',
  },
  'pii-vault': {
    color: 'red',
    icon: IconLock,
    label: 'PII Vault',
    description: 'Zero-trust PII encryption with user-controlled consent tokens',
    domain: 'Security',
  },
} as const;

type SubgraphId = keyof typeof SUBGRAPH_CONFIG;
type SubgraphStatus = keyof typeof STATUS_CONFIG;

interface SubgraphMetrics {
  requestsPerMinute: number;
  avgLatency: number; // ms
  errorRate: number; // percentage
  cacheHitRate: number; // percentage
  activeConnections: number;
}

interface SubgraphEntity {
  name: string;
  isKey: boolean;
  federatedFrom?: SubgraphId[];
}

interface SubgraphDependency {
  subgraph: SubgraphId;
  entities: string[];
  direction: 'PROVIDES' | 'REQUIRES';
}

interface Subgraph {
  id: SubgraphId;
  version: string;
  status: SubgraphStatus;
  endpoint: string;
  metrics: SubgraphMetrics;
  entities: SubgraphEntity[];
  dependencies: SubgraphDependency[];
  lastHealthCheck: string;
  uptime: number; // percentage
  primitives?: string[]; // Only for core
  // Backend metadata (from SubgraphInfo)
  displayName: string;
  description: string;
  icon: string;
  color: string;
  isLifeCritical: boolean;
  complianceFrameworks: string[];
}

// Static data representing the FERP federation architecture
// Source: supergraph config, router-local config, common/configs/seed/subgraphs configs
const MOCK_SUBGRAPHS: Subgraph[] = [
  // @ts-expect-error Missing property
  {
    id: 'core',
    version: '2.1.0',
    status: 'HEALTHY',
    endpoint: 'http://gateway:8080/graphql',
    metrics: {
      requestsPerMinute: 1250,
      avgLatency: 45,
      errorRate: 0.02,
      cacheHitRate: 94.5,
      activeConnections: 48,
    },
    entities: [
      { name: 'Workflow', isKey: true },
      { name: 'Template', isKey: true },
      { name: 'Dependency', isKey: true },
      { name: 'Policy', isKey: true },
      { name: 'Role', isKey: true },
      { name: 'Team', isKey: true },
      { name: 'Actor', isKey: true },
      { name: 'Validation', isKey: true },
      { name: 'Contract', isKey: true },
      { name: 'Constraint', isKey: true },
      { name: 'Instance', isKey: true },
      { name: 'Task', isKey: true },
      { name: 'Approval', isKey: true },
      { name: 'Document', isKey: true },
      { name: 'Queue', isKey: true },
      { name: 'Event', isKey: true },
      { name: 'Notification', isKey: true },
      { name: 'Trace', isKey: true },
      { name: 'Checkpoint', isKey: true },
      { name: 'Transaction', isKey: true },
      { name: 'Audit', isKey: true },
      { name: 'Analytics', isKey: true },
      { name: 'Schedule', isKey: true },
      { name: 'Resource', isKey: true },
      { name: 'Integration', isKey: true },
    ],
    dependencies: [
      { subgraph: 'fhir', entities: ['FhirPatient', 'FhirEncounter', 'FhirObservation'], direction: 'REQUIRES' },
      { subgraph: 'payroll', entities: ['PayrollRun', 'PayrollTimeEntry'], direction: 'REQUIRES' },
      { subgraph: 'erp', entities: ['ErpAcctgTrans', 'ErpAgreement'], direction: 'REQUIRES' },
    ],
    lastHealthCheck: new Date(Date.now() - 1000 * 30).toISOString(),
    uptime: 99.97,
    // Actual 25 primitives from ferp-core config, grouped by lifecycle phase
    primitives: [
      'Workflow', 'Template', 'Dependency', 'Policy', 'Role',
      'Team', 'Actor', 'Validation', 'Contract', 'Constraint',
      'Instance', 'Task', 'Approval', 'Document', 'Queue',
      'Event', 'Notification', 'Trace', 'Checkpoint', 'Transaction',
      'Audit', 'Analytics', 'Schedule', 'Resource', 'Integration',
    ],
  },
  // @ts-expect-error Missing property
  {
    id: 'fhir',
    version: '2.0.0',
    status: 'HEALTHY',
    endpoint: 'http://ferp-subgraphs:8080/fhir/graphql',
    metrics: {
      requestsPerMinute: 890,
      avgLatency: 62,
      errorRate: 0.01,
      cacheHitRate: 89.2,
      activeConnections: 32,
    },
    entities: [
      { name: 'FhirPatient', isKey: true },
      { name: 'FhirEncounter', isKey: true },
      { name: 'FhirObservation', isKey: true },
      { name: 'FhirCondition', isKey: true },
      { name: 'FhirAllergyIntolerance', isKey: true },
      { name: 'FhirAppointment', isKey: true },
      { name: 'FhirCarePlan', isKey: true },
      { name: 'FhirCareTeam', isKey: true },
      { name: 'FhirDiagnosticReport', isKey: true },
      { name: 'FhirProcedure', isKey: true },
    ],
    dependencies: [
      { subgraph: 'core', entities: ['Workflow', 'Task', 'Audit'], direction: 'PROVIDES' },
    ],
    lastHealthCheck: new Date(Date.now() - 1000 * 28).toISOString(),
    uptime: 99.95,
  },
  // @ts-expect-error Missing property
  {
    id: 'payroll',
    version: '2.0.0',
    status: 'HEALTHY',
    endpoint: 'http://ferp-subgraphs:8080/payroll/graphql',
    metrics: {
      requestsPerMinute: 456,
      avgLatency: 38,
      errorRate: 0.03,
      cacheHitRate: 92.1,
      activeConnections: 18,
    },
    entities: [
      { name: 'PayrollRun', isKey: true },
      { name: 'PayrollTimeEntry', isKey: true },
      { name: 'PayrollSalaryBand', isKey: true },
      { name: 'PayrollBankAccount', isKey: true },
      { name: 'PayrollBenefit', isKey: true },
      { name: 'PayrollBreak', isKey: true },
      { name: 'PayrollACAReport', isKey: true },
    ],
    dependencies: [
      { subgraph: 'core', entities: ['Workflow', 'Task', 'Policy'], direction: 'PROVIDES' },
      { subgraph: 'erp', entities: ['ErpAcctgTrans'], direction: 'REQUIRES' },
    ],
    lastHealthCheck: new Date(Date.now() - 1000 * 25).toISOString(),
    uptime: 99.89,
  },
  // @ts-expect-error Missing property
  {
    id: 'erp',
    version: '2.0.0',
    status: 'HEALTHY',
    endpoint: 'http://ferp-subgraphs:8080/erp/graphql',
    metrics: {
      requestsPerMinute: 678,
      avgLatency: 85,
      errorRate: 0.05,
      cacheHitRate: 88.5,
      activeConnections: 24,
    },
    entities: [
      { name: 'ErpAcctgTrans', isKey: true },
      { name: 'ErpAgreement', isKey: true },
      { name: 'ErpAffiliate', isKey: true },
      { name: 'ErpAccommodationClass', isKey: true },
      { name: 'ErpAcctgTransEntry', isKey: true },
      { name: 'ErpAddendum', isKey: true },
    ],
    dependencies: [
      { subgraph: 'core', entities: ['Workflow', 'Task', 'Audit'], direction: 'PROVIDES' },
      { subgraph: 'payroll', entities: ['PayrollTimeEntry'], direction: 'REQUIRES' },
    ],
    lastHealthCheck: new Date(Date.now() - 1000 * 32).toISOString(),
    uptime: 99.82,
  },
  // @ts-expect-error Missing property
  {
    id: 'wearables',
    version: '1.0.0',
    status: 'HEALTHY',
    endpoint: 'http://ferp-subgraphs:8080/wearables/graphql',
    metrics: {
      requestsPerMinute: 120,
      avgLatency: 42,
      errorRate: 0.01,
      cacheHitRate: 95.0,
      activeConnections: 8,
    },
    entities: [
      { name: 'WearableDevice', isKey: true },
      { name: 'WearableSession', isKey: true },
      { name: 'WearableCapture', isKey: true },
      { name: 'WearableVoiceCommand', isKey: true },
    ],
    dependencies: [
      { subgraph: 'core', entities: ['Workflow', 'Task'], direction: 'PROVIDES' },
      { subgraph: 'fhir', entities: ['FhirPatient'], direction: 'REQUIRES' },
    ],
    lastHealthCheck: new Date(Date.now() - 1000 * 20).toISOString(),
    uptime: 99.91,
  },
  // @ts-expect-error Missing property
  {
    id: 'website',
    version: '1.0.0',
    status: 'HEALTHY',
    endpoint: 'http://website-subgraph:8083/graphql',
    metrics: {
      requestsPerMinute: 210,
      avgLatency: 35,
      errorRate: 0.02,
      cacheHitRate: 96.0,
      activeConnections: 12,
    },
    entities: [
      { name: 'WebsiteSite', isKey: true },
      { name: 'WebsitePage', isKey: true },
      { name: 'WebsiteComponent', isKey: true },
      { name: 'WebsiteDeployment', isKey: true },
    ],
    dependencies: [
      { subgraph: 'core', entities: ['Template'], direction: 'PROVIDES' },
    ],
    lastHealthCheck: new Date(Date.now() - 1000 * 18).toISOString(),
    uptime: 99.98,
  },
  // @ts-expect-error Missing property
  {
    id: 'portal',
    version: '1.0.0',
    status: 'HEALTHY',
    endpoint: 'http://ferp-subgraphs:8080/portal/graphql',
    metrics: {
      requestsPerMinute: 85,
      avgLatency: 48,
      errorRate: 0.01,
      cacheHitRate: 90.0,
      activeConnections: 6,
    },
    entities: [
      { name: 'PortalFormSchema', isKey: true },
      { name: 'PortalSubmission', isKey: true },
      { name: 'PortalIntake', isKey: true },
    ],
    dependencies: [
      { subgraph: 'core', entities: ['Workflow', 'Instance', 'Document'], direction: 'PROVIDES' },
    ],
    lastHealthCheck: new Date(Date.now() - 1000 * 15).toISOString(),
    uptime: 99.99,
  },
  // @ts-expect-error Missing property
  {
    id: 'pii-vault',
    version: '1.0.0',
    status: 'HEALTHY',
    endpoint: 'http://pii-vault:8090/graphql',
    metrics: {
      requestsPerMinute: 340,
      avgLatency: 28,
      errorRate: 0.00,
      cacheHitRate: 97.0,
      activeConnections: 16,
    },
    entities: [
      { name: 'PiiEncryptedField', isKey: true },
      { name: 'PiiConsentToken', isKey: true },
      { name: 'PiiAccessLog', isKey: true },
    ],
    dependencies: [
      { subgraph: 'core', entities: ['Audit'], direction: 'PROVIDES' },
      { subgraph: 'fhir', entities: ['FhirPatient'], direction: 'REQUIRES' },
    ],
    lastHealthCheck: new Date(Date.now() - 1000 * 10).toISOString(),
    uptime: 99.99,
  },
];

export function SubgraphConstellation(): React.ReactElement {
  const { tenant } = useAuth();
  const { config, loading, refetch } = useTenantSubgraphConfig(tenant?.id ?? null);
  const { metrics: routerMetrics, loading: metricsLoading } = useRouterMetrics(5000); // Refresh every 5s
  const [selectedSubgraph, setSelectedSubgraph] = useState<Subgraph | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('federation');

  // Transform backend SubgraphInfo to local Subgraph format with static fallbacks for metrics
  const subgraphs: Subgraph[] = useMemo(() => {
    if (!config) return [];

    return config.subgraphs.map((sg) => {
      // Static fallbacks for metrics (will be replaced with real Prometheus data later)
      const mockMetrics = MOCK_SUBGRAPHS.find((m) => m.id === sg.subgraphId)?.metrics || {
        requestsPerMinute: 0,
        avgLatency: 50,
        errorRate: 0.01,
        cacheHitRate: 90,
        activeConnections: 0,
      };

      const mockEntities = MOCK_SUBGRAPHS.find((m) => m.id === sg.subgraphId)?.entities || [];
      const mockDependencies = MOCK_SUBGRAPHS.find((m) => m.id === sg.subgraphId)?.dependencies || [];
      const mockPrimitives = MOCK_SUBGRAPHS.find((m) => m.id === sg.subgraphId)?.primitives;

      return {
        id: sg.subgraphId as SubgraphId,
        version: '2.0.0', // TODO: Get from backend once version field is added
        status: 'HEALTHY' as SubgraphStatus, // TODO: Get from health check API
        endpoint: `http://ferp-subgraphs:8080/${sg.subgraphId}/graphql`,
        metrics: mockMetrics,
        entities: mockEntities,
        dependencies: mockDependencies,
        lastHealthCheck: new Date().toISOString(),
        uptime: 99.9,
        primitives: mockPrimitives,
        // Backend metadata (from SubgraphInfo)
        displayName: sg.displayName,
        description: sg.description,
        icon: sg.icon,
        color: sg.color,
        isLifeCritical: sg.isLifeCritical,
        complianceFrameworks: sg.complianceFrameworks,
      };
    });
  }, [config]);

  // Calculate overall system health
  const systemHealth = useMemo(() => {
    const healthyCount = subgraphs.filter((s) => s.status === 'HEALTHY').length;
    const totalCount = subgraphs.length;
    const healthPercentage = (healthyCount / totalCount) * 100;
    const avgUptime = subgraphs.reduce((sum, s) => sum + s.uptime, 0) / totalCount;
    const totalRPM = subgraphs.reduce((sum, s) => sum + s.metrics.requestsPerMinute, 0);
    const avgLatency = subgraphs.reduce((sum, s) => sum + s.metrics.avgLatency, 0) / totalCount;

    return {
      healthyCount,
      totalCount,
      healthPercentage,
      avgUptime,
      totalRPM,
      avgLatency,
      status: healthPercentage === 100 ? 'HEALTHY' : healthPercentage >= 75 ? 'DEGRADED' : 'UNHEALTHY',
    };
  }, [subgraphs]);

  const openDetailModal = (subgraph: Subgraph) => {
    setSelectedSubgraph(subgraph);
    setDetailModalOpen(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Show loading state while fetching initial data
  if (loading) {
    return (
      <Center p="xl">
        <Stack ta="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading subgraph configuration...</Text>
        </Stack>
      </Center>
    );
  }

  if (!config || subgraphs.length === 0) {
    return (
      <Alert color="yellow" title="No data available">
        Unable to load subgraph configuration. Make sure the backend is running.
      </Alert>
    );
  }

  const formatLatency = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusIcon = (status: SubgraphStatus) => {
    const IconComponent = STATUS_CONFIG[status].icon;
    return <IconComponent size={14} />;
  };

  const getSubgraphIcon = (id: SubgraphId) => {
    const IconComponent = SUBGRAPH_CONFIG[id].icon;
    return <IconComponent size={20} />;
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between" ta="left">
        <div>
          <Title order={2}>Federation & Monitoring</Title>
          <Text c="dimmed" size="sm">
            Federated GraphQL architecture visualization and error monitoring
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            loading={isRefreshing}
            onClick={handleRefresh}
          >
            Refresh Health
          </Button>
        </Group>
      </Group>

      {/* Main Tabs: Federation vs Errors */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'federation')}>
        <Tabs.List>
          <Tabs.Tab value="federation">Federation</Tabs.Tab>
          <Tabs.Tab value="errors">Error Monitor</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="federation" pt="md">
          <Stack gap="lg">

      {/* System Health Alert */}
      {systemHealth.status !== 'HEALTHY' && (
        <Alert
          icon={<IconAlertTriangle size={20} />}
          title="Federation Health Warning"
          color={systemHealth.status === 'DEGRADED' ? 'yellow' : 'red'}
          variant="light"
        >
          {systemHealth.healthyCount} of {systemHealth.totalCount} subgraphs are healthy. Some
          subgraphs are experiencing issues that may affect system performance.
        </Alert>
      )}

      {/* Apollo Router Card */}
      <Card withBorder p="lg" radius="md" style={{ borderColor: 'var(--mantine-color-blue-4)' }}>
        <Group justify="space-between" mb="md">
          <Group>
            <ThemeIcon size="xl" variant="gradient" gradient={{ from: 'blue', to: 'violet' }}>
              <IconCloud size={28} />
            </ThemeIcon>
            <div>
              <Text size="lg" fw={700}>
                Apollo Router
              </Text>
              <Text size="xs" c="dimmed">
                Supergraph v{routerMetrics?.supergraphVersion || '...'} &middot; Federation 2.7
              </Text>
            </div>
          </Group>
          <Badge
            size="lg"
            color={systemHealth.status === 'HEALTHY' ? 'green' : systemHealth.status === 'DEGRADED' ? 'yellow' : 'red'}
            leftSection={getStatusIcon(systemHealth.status as SubgraphStatus)}
          >
            {STATUS_CONFIG[systemHealth.status as SubgraphStatus].label}
          </Badge>
        </Group>

        {metricsLoading && !routerMetrics ? (
          <Center p="md">
            <Loader size="sm" />
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="md">
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase">
                Requests/min
              </Text>
              <Text size="xl" fw={700}>
                {routerMetrics?.requestsPerMinute.toLocaleString() || '0'}
              </Text>
            </Paper>
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase">
                Avg Latency
              </Text>
              <Text size="xl" fw={700}>
                {routerMetrics ? formatLatency(routerMetrics.avgLatency) : '...'}
              </Text>
            </Paper>
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase">
                Error Rate
              </Text>
              <Text
                size="xl"
                fw={700}
                c={routerMetrics && routerMetrics.errorRate > 1 ? 'red' : 'green'}
              >
                {routerMetrics?.errorRate || 0}%
              </Text>
            </Paper>
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase">
                Cache Hit Rate
              </Text>
              <Text size="xl" fw={700} c="green">
                {routerMetrics?.cacheHitRate || 0}%
              </Text>
            </Paper>
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase">
                Active Queries
              </Text>
              <Text size="xl" fw={700}>
                {routerMetrics?.activeQueries || 0}
              </Text>
            </Paper>
          </SimpleGrid>
        )}
      </Card>

      {/* Subgraph Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {subgraphs.map((subgraph) => {
          const IconComponent = getIcon(subgraph.icon);
          return (
            <Card
              key={subgraph.id}
              withBorder
              p="lg"
              radius="md"
              style={{
                borderColor:
                  subgraph.status !== 'HEALTHY'
                    ? `var(--mantine-color-${STATUS_CONFIG[subgraph.status].color}-4)`
                    : undefined,
                cursor: 'pointer',
              }}
              onClick={() => openDetailModal(subgraph)}
            >
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon size="lg" variant="light" color={subgraph.color}>
                    <IconComponent size={20} />
                  </ThemeIcon>
                  <div>
                    <Group gap={4}>
                      <Text fw={600}>{subgraph.displayName}</Text>
                      <Badge size="xs" variant="outline">
                        v{subgraph.version}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {subgraph.id}
                    </Text>
                  </div>
                </Group>
                <Badge
                  color={STATUS_CONFIG[subgraph.status].color}
                  leftSection={getStatusIcon(subgraph.status)}
                >
                  {STATUS_CONFIG[subgraph.status].label}
                </Badge>
              </Group>

              <Text size="sm" c="dimmed" mb="md">
                {subgraph.description}
              </Text>

              <SimpleGrid cols={4} spacing="xs">
                <div>
                  <Text size="xs" c="dimmed">
                    RPM
                  </Text>
                  <Text size="sm" fw={600}>
                    {subgraph.metrics.requestsPerMinute}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Latency
                  </Text>
                  <Text size="sm" fw={600}>
                    {formatLatency(subgraph.metrics.avgLatency)}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Errors
                  </Text>
                  <Text size="sm" fw={600} c={subgraph.metrics.errorRate > 1 ? 'red' : 'green'}>
                    {subgraph.metrics.errorRate}%
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Uptime
                  </Text>
                  <Text size="sm" fw={600} c={subgraph.uptime > 99 ? 'green' : 'yellow'}>
                    {subgraph.uptime}%
                  </Text>
                </div>
              </SimpleGrid>

              <Divider my="sm" />

              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  {subgraph.entities.length} entities
                </Text>
                <Text size="xs" c="dimmed">
                  {subgraph.dependencies.length} dependencies
                </Text>
              </Group>
            </Card>
          );
        })}
      </SimpleGrid>

      {/* Federation Connections Visualization */}
      <Card withBorder p="lg">
        <Text fw={600} mb="md">
          Entity Federation Map
        </Text>
        <Text size="sm" c="dimmed" mb="lg">
          How entities are shared across subgraphs for unified queries
        </Text>

        <Stack gap="md">
          {subgraphs.map((subgraph) => {
            const IconComponent = getIcon(subgraph.icon);
            const providedEntities = subgraph.dependencies
              .filter((d) => d.direction === 'PROVIDES')
              .flatMap((d) => d.entities);
            const requiredEntities = subgraph.dependencies
              .filter((d) => d.direction === 'REQUIRES')
              .flatMap((d) => d.entities);

            return (
              <Paper key={subgraph.id} withBorder p="md" radius="md">
                <Group mb="sm">
                  <ThemeIcon size="sm" variant="light" color={subgraph.color}>
                    <IconComponent size={16} />
                  </ThemeIcon>
                  <Text fw={600}>{subgraph.displayName}</Text>
                </Group>

                <SimpleGrid cols={2}>
                  {providedEntities.length > 0 && (
                    <div>
                      <Text size="xs" c="green" fw={600} mb="xs">
                        Provides to Federation:
                      </Text>
                      <Group gap={4}>
                        {providedEntities.map((entity) => (
                          <Badge key={entity} size="xs" variant="dot" color="green">
                            {entity}
                          </Badge>
                        ))}
                      </Group>
                    </div>
                  )}

                  {requiredEntities.length > 0 && (
                    <div>
                      <Text size="xs" c="blue" fw={600} mb="xs">
                        Requires from Federation:
                      </Text>
                      <Group gap={4}>
                        {requiredEntities.map((entity) => (
                          <Badge key={entity} size="xs" variant="dot" color="blue">
                            {entity}
                          </Badge>
                        ))}
                      </Group>
                    </div>
                  )}
                </SimpleGrid>
              </Paper>
            );
          })}
        </Stack>
      </Card>

      {/* Subgraph Detail Modal */}
      <Modal
        opened={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={
          selectedSubgraph && (
            <Group>
              <ThemeIcon
                size="md"
                variant="light"
                color={selectedSubgraph.color}
              >
                {React.createElement(getIcon(selectedSubgraph.icon), { size: 20 })}
              </ThemeIcon>
              <div>
                <Text fw={600}>{selectedSubgraph.displayName}</Text>
                <Text size="xs" c="dimmed">
                  v{selectedSubgraph.version}
                </Text>
              </div>
            </Group>
          )
        }
        size="lg"
      >
        {selectedSubgraph && (
          <Tabs defaultValue="overview">
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="entities">Entities ({selectedSubgraph.entities.length})</Tabs.Tab>
              <Tabs.Tab value="dependencies">Dependencies</Tabs.Tab>
              {selectedSubgraph.primitives && <Tabs.Tab value="primitives">25 Primitives</Tabs.Tab>}
            </Tabs.List>

            <Tabs.Panel value="overview" pt="md">
              <Stack gap="md">
                <Text>{selectedSubgraph.description}</Text>

                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
                    Endpoint
                  </Text>
                  <Code>{selectedSubgraph.endpoint}</Code>
                </Paper>

                <SimpleGrid cols={2}>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Status
                    </Text>
                    <Badge color={STATUS_CONFIG[selectedSubgraph.status].color}>
                      {STATUS_CONFIG[selectedSubgraph.status].label}
                    </Badge>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Uptime
                    </Text>
                    <Text fw={600}>{selectedSubgraph.uptime}%</Text>
                  </div>
                </SimpleGrid>

                <Divider />

                <Text fw={600}>Performance Metrics</Text>
                <SimpleGrid cols={2}>
                  <Paper withBorder p="sm" radius="md">
                    <Text size="xs" c="dimmed">
                      Requests/min
                    </Text>
                    <Text size="lg" fw={700}>
                      {selectedSubgraph.metrics.requestsPerMinute}
                    </Text>
                  </Paper>
                  <Paper withBorder p="sm" radius="md">
                    <Text size="xs" c="dimmed">
                      Avg Latency
                    </Text>
                    <Text size="lg" fw={700}>
                      {formatLatency(selectedSubgraph.metrics.avgLatency)}
                    </Text>
                  </Paper>
                  <Paper withBorder p="sm" radius="md">
                    <Text size="xs" c="dimmed">
                      Error Rate
                    </Text>
                    <Text
                      size="lg"
                      fw={700}
                      c={selectedSubgraph.metrics.errorRate > 1 ? 'red' : 'green'}
                    >
                      {selectedSubgraph.metrics.errorRate}%
                    </Text>
                  </Paper>
                  <Paper withBorder p="sm" radius="md">
                    <Text size="xs" c="dimmed">
                      Cache Hit Rate
                    </Text>
                    <Text size="lg" fw={700} c="green">
                      {selectedSubgraph.metrics.cacheHitRate}%
                    </Text>
                  </Paper>
                </SimpleGrid>

                <Text size="xs" c="dimmed">
                  Last health check: {new Date(selectedSubgraph.lastHealthCheck).toLocaleString()}
                </Text>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="entities" pt="md">
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  GraphQL entities exposed by this subgraph
                </Text>
                <SimpleGrid cols={3}>
                  {selectedSubgraph.entities.map((entity) => (
                    <Paper key={entity.name} withBorder p="sm" radius="md">
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>
                          {entity.name}
                        </Text>
                        {entity.isKey && (
                          <Badge size="xs" color="blue">
                            @key
                          </Badge>
                        )}
                      </Group>
                      {entity.federatedFrom && (
                        <Text size="xs" c="dimmed" mt={4}>
                          Extended from: {entity.federatedFrom.join(', ')}
                        </Text>
                      )}
                    </Paper>
                  ))}
                </SimpleGrid>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="dependencies" pt="md">
              <Stack gap="md">
                {selectedSubgraph.dependencies.length > 0 ? (
                  selectedSubgraph.dependencies.map((dep, idx) => {
                    const depSubgraph = subgraphs.find(s => s.id === dep.subgraph);
                    return (
                    <Paper key={idx} withBorder p="md" radius="md">
                      <Group justify="space-between" mb="xs">
                        <Group>
                          <ThemeIcon
                            size="sm"
                            variant="light"
                            color={depSubgraph?.color || 'gray'}
                          >
                            {depSubgraph ? React.createElement(getIcon(depSubgraph.icon), { size: 16 }) : null}
                          </ThemeIcon>
                          <Text fw={500}>{depSubgraph?.displayName || dep.subgraph}</Text>
                        </Group>
                        <Badge color={dep.direction === 'PROVIDES' ? 'green' : 'blue'} variant="light">
                          {dep.direction === 'PROVIDES' ? 'Extends' : 'Uses'}
                        </Badge>
                      </Group>
                      <Group gap={4}>
                        {dep.entities.map((entity) => (
                          <Badge key={entity} size="xs" variant="outline">
                            {entity}
                          </Badge>
                        ))}
                      </Group>
                    </Paper>
                  );
                  })
                ) : (
                  <Text c="dimmed" ta="center" py="xl">
                    No external dependencies.
                  </Text>
                )}
              </Stack>
            </Tabs.Panel>

            {selectedSubgraph.primitives && (
              <Tabs.Panel value="primitives" pt="md">
                <Stack gap="md">
                  <Text size="sm" c="dimmed">
                    FERP 25-primitive system organized by lifecycle phase
                  </Text>

                  {['DESIGN', 'CONFIGURE', 'EXECUTE', 'MONITOR', 'OPTIMIZE'].map((phase, phaseIdx) => {
                    const phasePrimitives = selectedSubgraph.primitives!.slice(phaseIdx * 5, (phaseIdx + 1) * 5);
                    const phaseColors = ['blue', 'violet', 'cyan', 'yellow', 'red'];
                    return (
                      <Paper key={phase} withBorder p="md" radius="md">
                        <Badge color={phaseColors[phaseIdx]} mb="xs">
                          {phase}
                        </Badge>
                        <Group gap={4} mt="xs">
                          {phasePrimitives.map((primitive) => (
                            <Badge key={primitive} variant="outline" size="sm">
                              {primitive}
                            </Badge>
                          ))}
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              </Tabs.Panel>
            )}
          </Tabs>
        )}
      </Modal>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="errors" pt="md">
          <ErrorTracePanel />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
